import { BlogPost } from "../components/BlogCard";
import React from "react";
import { z } from "zod";

export const frontmatterSchema = z.object({
  id: z.string(),
  title: z.string(),
  date: z.string(),
  category: z.string(),
  author: z.string(),
  excerpt: z.string().optional(),
  readTime: z.string(),
  featured: z.boolean().default(false),
  draft: z.boolean().default(false),
});

export type Frontmatter = z.infer<typeof frontmatterSchema>;

// Shape of a dynamically‑imported MDX module
interface MdxModule {
  frontmatter?: unknown;
  default: React.ComponentType;
}

// Use import.meta.glob with eager: true option instead of globEager
const mdxPostModules = import.meta.glob<MdxModule>("./blog/**/*.mdx", {
  eager: true,
});

// This map will hold extracted metadata and the MDX component for each post, keyed by id
const blogPostMeta: Record<string, BlogPost> = {};

Object.entries(mdxPostModules).forEach(([path, mod]) => {
  // Use provided frontmatter if it exists
  const frontmatter = mod.frontmatter;

  const fm = frontmatterSchema.parse(frontmatter);

  blogPostMeta[fm.id] = { ...fm, MDXContent: mod.default };

});

// Helper for static lists if needed elsewhere
export const blogPostsStatic = Object.keys(blogPostMeta);

// Return all blog posts metadata (synchronous, already loaded!)
export const getBlogPosts = (): BlogPost[] => {
  // Return all published posts, sorted by date (descending)
  return Object.values(blogPostMeta)
    .filter((post) => !post.draft)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
};

export const getFeaturedBlogPosts = (): BlogPost[] => {
  return getBlogPosts().filter((post) => post.featured);
};

// Returns post with its MDXContent (synchronous)
export const getBlogPostById = (id: string): BlogPost | undefined => {
  return blogPostMeta[id];
};
